import { Check } from "lucide-react";
import { useMemo } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useMemberPermissions } from "@/hooks/useMemberPermissions";
import { useMemberCalendarAccess } from "@/hooks/useMemberCalendarAccess";
import { useGoogleCalendars } from "@/hooks/useGoogleCalendars";
import { useAppointmentsCalendarFilter } from "@/contexts/AppointmentsCalendarFilterContext";
import { isGcalMultiCalendarEnabled } from "@/lib/gcal-feature-flags";
import {
  getCalendarAccentColor,
  listEnabledSyncCalendars,
} from "@/lib/gcal-calendar-colors";
import {
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";

export function useAppointmentsCalendarSidebarData() {
  const { user } = useAuth();
  const { workspaceId } = useWorkspace(user?.id);
  const { memberId, canSeeAllCalendars } = useMemberPermissions();
  const { allowedGoogleCalendarIds } = useMemberCalendarAccess({
    workspaceId,
    memberId,
    canSeeAllCalendars,
  });
  const { connections: gcalConnections, isLoading } = useGoogleCalendars();
  const { isMultiCalendarEnabled } = useAppointmentsCalendarFilter();

  const visibleSyncCalendars = useMemo(
    () =>
      isGcalMultiCalendarEnabled()
        ? listEnabledSyncCalendars(gcalConnections, allowedGoogleCalendarIds)
        : [],
    [gcalConnections, allowedGoogleCalendarIds],
  );

  const orderedCalendarIds = useMemo(
    () => visibleSyncCalendars.map((cal) => cal.calendar_id),
    [visibleSyncCalendars],
  );

  const shouldShowSubmenu =
    isMultiCalendarEnabled && visibleSyncCalendars.length >= 2;

  return {
    shouldShowSubmenu,
    visibleSyncCalendars,
    orderedCalendarIds,
    isLoading,
  };
}

/** Sub-itens com checkboxes coloridos (estilo Conversas). */
export function AppointmentsCalendarSubmenu() {
  const { shouldShowSubmenu, visibleSyncCalendars, orderedCalendarIds, isLoading } =
    useAppointmentsCalendarSidebarData();
  const { toggleCalendarVisibility, isCalendarVisible } = useAppointmentsCalendarFilter();

  if (!shouldShowSubmenu) {
    return null;
  }

  return (
    <SidebarMenuSub className="mt-0.5 mx-2 gap-0 border-l px-1.5 py-0">
      {isLoading && visibleSyncCalendars.length === 0 ? (
        <li className="px-1 py-1 text-[11px] text-muted-foreground">Carregando...</li>
      ) : (
        visibleSyncCalendars.map((cal) => {
          const accent =
            getCalendarAccentColor(cal.calendar_id, orderedCalendarIds) ?? "#6b7280";
          const visible = isCalendarVisible(cal.calendar_id);
          const label = cal.display_name || cal.calendar_id;

          return (
            <SidebarMenuSubItem key={cal.calendar_id}>
              <SidebarMenuSubButton
                asChild
                size="sm"
                className="h-auto min-h-0 py-0.5 px-1.5 leading-tight"
              >
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={visible}
                  aria-label={`${visible ? "Ocultar" : "Mostrar"} agenda ${label}`}
                  onClick={() => toggleCalendarVisibility(cal.calendar_id)}
                  className={cn(
                    "flex w-full items-center gap-1.5 text-left",
                    !visible && "opacity-60",
                  )}
                >
                  <span
                    className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-[3px] border"
                    style={{
                      backgroundColor: visible ? accent : "transparent",
                      borderColor: accent,
                    }}
                  >
                    {visible && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-[11px] font-medium leading-snug">
                    {label}
                  </span>
                </button>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
          );
        })
      )}
    </SidebarMenuSub>
  );
}
